import * as React from 'react';
import { identity } from './identity.ts';

/**
 * Extracts up to `count` elements from the input array.
 */
export function take<T>(inputArray: Array<T>, count: number, offset: number = 0): Array<T> {
  return inputArray.slice(offset, count + offset);
}

/**
 * Extracts the last `count` elements from the input array.
 */
export function takeLast<T>(inputArray: Array<T>, count: number): Array<T> {
  return inputArray.slice(-count);
}

/**
 * Returns an array's very last element.
 * The return type is possibly undefined, except if the array is a NonEmptyArray.
 */
export const last = <T extends unknown[] | readonly unknown[]>(
  array: T
): T extends NonEmptyArray<infer E> ? E : T extends Array<infer E> ? E | undefined : never =>
  array[array.length - 1] as any;

export const isLastIndex = (array: unknown[], index: number) => index === array.length - 1;

/**
 * Returns an array's first element.
 * The return type is possibly undefined, except if the array is a NonEmptyArray.
 */
export const first = <T extends unknown[]>(
  array: T
): T extends NonEmptyArray<infer E> ? E : T extends Array<infer E> ? E | undefined : never =>
  array[0] as any;

/**
 * Returns all elements of an array, except the first.
 */
export const tail = <T extends unknown[]>(array: T): Tail<T> => array.slice(1) as any;

/**
 * Returns a shuffled array with the Fisher-Yates algorithm.
 */
export const shuffleArray = <T,>(a: T[]): T[] => {
  a = [...a]; // make it side effect free

  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }

  return a;
};

/**
 * Reverses the array without mutating the original (which JS' native Array.reverse does).
 */
export function reverse<T>(inputArray: Array<T>): Array<T> {
  return [...inputArray].reverse();
}

/**
 * Type for an array that must have at least one element.
 */
export type NonEmptyArray<T> = [T, ...T[]];

/**
 * Type casts a normal array to a NonEmptyArray. This is sadly sometimes needed, because of lacking
 * native TS types. e.g. .map() does always return an array, even if the input was a tuple / NonEmptyArray.
 */
export const nonEmptyArray = <T,>(array: T[]): NonEmptyArray<T> => array as NonEmptyArray<T>;

/**
 * Type guard to check whether an array has at least one element and thus is a NonEmptyArray.
 */
export const isNonEmptyArray = <T,>(array: T[] | undefined | null): array is NonEmptyArray<T> =>
  !!array && hasAtLeastLength(array, 1);

export const isEmptyArray = (array: unknown[] | undefined | null): array is [] =>
  !!array && array.length === 0;

/**
 * Infers the type of an array's elements.
 *
 * Array<string> becomes string
 */
export type ArrayElement<ArrayType extends readonly unknown[]> =
  ArrayType extends readonly (infer ElementType)[] ? ElementType : never;

/**
 * Type for a tuple of n elements.
 *
 * From https://stackoverflow.com/a/52490977/6419868
 */
export type Tuple<T, N extends number> = N extends N
  ? number extends N
    ? T[]
    : _TupleOf<T, N, []>
  : never;
type _TupleOf<T, N extends number, R extends unknown[]> = R['length'] extends N
  ? R
  : _TupleOf<T, N, [T, ...R]>;

/**
 * Type for a tuple's tail
 *
 * Type `[string, number, object]` becomes `[number, object]`
 */
export type Tail<T extends any[]> = T extends [any, ...infer R] ? R : never;

/**
 * Type guard to check whether an array has an exact length and thus can be handled as a tuple.
 *
 * Example usage:
 * ```
 * if (hasExactLength(someArray, 2)) {
 *   someArray[1]; // <-- No unchecked index error 👍
 * }
 * ```
 */
export const hasExactLength = <T, N extends number>(array: T[], length: N): array is Tuple<T, N> =>
  array.length === length;

/**
 * Type guard to check whether an array has a specific length **or more**.
 * This is similar to hasExactLength(), except that the array is not limited in its length.
 *
 * Example usage:
 * ```
 * if (hasAtLeastLength(someArray, 2)) {
 *   someArray[1]; // <-- No unchecked index error 👍
 *   someArray[2]; // <-- Still possible, but unchecked index error will appear
 * }
 * ```
 */
export const hasAtLeastLength = <T, N extends number>(
  array: T[],
  length: N
): array is [...Tuple<T, N>, ...T[]] => array.length >= length;

/**
 * Compares whether the elements of two arrays are equal, using `===`.
 */
export const arraysEqual = (a: Array<unknown>, b: Array<unknown>): boolean =>
  a.length === b.length && a.every((value, index) => b[index] === value);

export const arraysHaveSameElementsUnordered = (a: Array<unknown>, b: Array<unknown>): boolean =>
  a.every(value => b.includes(value)) && b.every(value => a.includes(value));

/**
 * Immutable variant of `array.splice(index, 1)`.
 * Returns an array with the given index removed.
 */
export const removeArrayIndex = <T,>(array: T[], index: number): T[] => [
  ...array.slice(0, index),
  ...array.slice(index + 1)
];

export const insertAtArrayIndex = <T,>(array: T[], index: number, newElement: T): T[] => [
  ...array.slice(0, index),
  newElement,
  ...array.slice(index)
];

/**
 * Immutably move an element from one index to another.
 */
export const moveArrayIndex = <T,>(array: T[], fromIndex: number, toIndex: number): T[] => {
  const movedElement = array[fromIndex];
  if (movedElement) {
    const result = removeArrayIndex(array, fromIndex);
    result.splice(toIndex, 0, movedElement);
    return result;
  } else {
    return array;
  }
};

/**
 * Immutably set the array index.
 */
export const updateArrayIndex = <T,>(array: T[], index: number, newElement: T): T[] => {
  const newArray = [...array];
  newArray[index] = newElement;
  return newArray;
};

/**
 * Groups an array by a key.
 *
 * ```
 * groupBy(['one', 'two', 'three'], s => s.length)
 * => Map(3 -> ["one", "two"], 5 -> ["three"])
 * ```
 *
 * Inspired from: https://stackoverflow.com/a/47752730
 */
export const groupBy = <T, K>(array: T[], f: (element: T) => K): Map<K, NonEmptyArray<T>> =>
  array.reduce((result, element) => {
    const key = f(element);
    return result.set(key, [...(result.get(key) || []), element]);
  }, new Map());

/**
 * Removes duplicate values in an array, based on a custom identifier.
 */
export const distinct = <T,>(array: T[], f: (element: T) => unknown = identity): T[] =>
  Array.from(groupBy(array, f).values()).map(grouped => grouped[0]);

/**
 * Works like native join() but with jsx instead of strings.
 * @param separator Can be passed a function to have varying separators, based on the element's index.
 */
export const joinJsx = (
  elements: React.ReactNode[],
  separator:
    | React.ReactNode
    | ((options: {
        prevItem: { index: number };
        nextItem: { index: number };
        isLast: boolean;
      }) => React.ReactNode)
): React.ReactNode => {
  const filteredElements = elements.filter(element => element);
  return filteredElements.reduce<React.ReactNode>(
    (result, element, index) => {
      const jsxSeparator: React.ReactNode =
        typeof separator === 'function'
          ? separator({
              prevItem: { index: index - 1 },
              nextItem: { index },
              isLast: index === filteredElements.length - 1
            })
          : separator;

      return (
        <>
          {index > 0 && (
            <>
              {result}
              {jsxSeparator}
            </>
          )}
          {element}
        </>
      );
    },
    <></>
  );
};

/**
 * Just like scala's collectFirst().
 * Maps the elements and returns the first one that matches the filter.
 * Basically a performant version of `.map().find(e => e !== undefined)`,
 * except that it doesn't map all elements before finding one.
 *
 * @param filter By default, the first element is returned, that is not undefined and not null.
 */
export const collectFirst = <T, R>(
  /**
   * null and undefined for easier usage with graphql
   */
  array: T[] | null | undefined,
  fn: (element: T) => R,
  filter: (result: R) => boolean = r => r !== undefined && r !== null
): R | undefined => {
  if (isNonEmptyArray(array)) {
    let match: { found: false } | { found: true; result: R } = { found: false };
    for (let i = 0; i < array.length && !match.found; i++) {
      const element = array[i];
      if (element) {
        const result = fn(element);
        if (filter(result)) {
          match = { found: true, result };
        }
      }
    }
    return match.found ? match.result : undefined;
  } else {
    return undefined;
  }
};

/**
 * Calculate the sum of an array of numbers.
 */
export const sum = (array: number[]): number => array.reduce((sum, current) => sum + current, 0);
